/**
 * Guest-list dedup and head-count helpers shared between the SPA and plan limits.
 *
 * Mirrors the invitation "Osoby" stat: one count per invitation host unit
 * (host + plus-one + children), excluding linked companion/child docs and
 * duplicate wedding-couple placeholders.
 */
import { headCount } from "./schemas/guest";
/** Legacy seeded placeholder names before `weddingRole` existed. */
export function isLegacyGroomName(guest) {
    return guest.firstName.trim() === "Pan" && guest.lastName.trim() === "Młody";
}
export function isLegacyBrideName(guest) {
    return guest.firstName.trim() === "Pani" && guest.lastName.trim() === "Młoda";
}
/** Returns explicit or legacy-inferred wedding couple role. */
export function resolveWeddingRole(guest) {
    if (guest.weddingRole === "groom" || guest.weddingRole === "bride") {
        return guest.weddingRole;
    }
    if (isLegacyGroomName(guest))
        return "groom";
    if (isLegacyBrideName(guest))
        return "bride";
    return null;
}
export function isWeddingCoupleGuest(guest) {
    return resolveWeddingRole(guest) !== null;
}
export function isWeddingCouplePair(a, b) {
    const roleA = resolveWeddingRole(a);
    const roleB = resolveWeddingRole(b);
    if (!roleA || !roleB || roleA === roleB)
        return false;
    return a.partnerId === b.id && b.partnerId === a.id;
}
function getAllWeddingRoleGuests(allGuests, role) {
    return allGuests.filter((g) => resolveWeddingRole(g) === role);
}
function compareGuestCreatedAt(a, b) {
    return a.createdAt.localeCompare(b.createdAt);
}
/** Pick the single authoritative groom + bride from duplicates. */
export function pickCanonicalCouplePair(allGuests) {
    const grooms = getAllWeddingRoleGuests(allGuests, "groom");
    const brides = getAllWeddingRoleGuests(allGuests, "bride");
    for (const groom of [...grooms].sort(compareGuestCreatedAt)) {
        for (const bride of [...brides].sort(compareGuestCreatedAt)) {
            if (groom.partnerId === bride.id && bride.partnerId === groom.id) {
                return { groom, bride };
            }
        }
    }
    const pickOne = (candidates, role) => {
        if (candidates.length === 0)
            return null;
        const explicit = candidates.filter((g) => g.weddingRole === role);
        const pool = explicit.length > 0 ? explicit : candidates;
        return [...pool].sort(compareGuestCreatedAt)[0] ?? null;
    };
    return { groom: pickOne(grooms, "groom"), bride: pickOne(brides, "bride") };
}
export function getCanonicalCoupleIds(allGuests) {
    const { groom, bride } = pickCanonicalCouplePair(allGuests);
    const ids = new Set();
    if (groom)
        ids.add(groom.id);
    if (bride)
        ids.add(bride.id);
    return ids;
}
/** Groom first, then bride — only the canonical system couple docs. */
export function getWeddingCoupleGuests(allGuests) {
    const { groom, bride } = pickCanonicalCouplePair(allGuests);
    return [groom, bride].filter((g) => g != null);
}
/** Normalized first|last key — same convention as findDuplicate in filters.ts. */
export function personNameKey(firstName, lastName) {
    return `${firstName.trim().toLowerCase()}|${lastName.trim().toLowerCase()}`;
}
/** Guest ids referenced as someone else's linked companion or child. */
export function collectCompanionGuestIds(guests) {
    const ids = new Set();
    for (const g of guests) {
        const plusOneId = g.plusOne?.guestId?.trim();
        if (plusOneId)
            ids.add(plusOneId);
        for (const c of g.children ?? []) {
            const childId = c.guestId?.trim();
            if (childId)
                ids.add(childId);
        }
    }
    return ids;
}
function collectLegacyCompanionNameKeys(guests) {
    const keys = new Set();
    for (const g of guests) {
        const p = g.plusOne;
        if (!p?.firstName?.trim() || p.guestId?.trim())
            continue;
        keys.add(personNameKey(p.firstName, p.lastName ?? ""));
    }
    return keys;
}
function collectLegacyChildFirstNameKeys(guests) {
    const keys = new Set();
    for (const g of guests) {
        for (const c of g.children ?? []) {
            if (!c.firstName?.trim() || c.guestId?.trim())
                continue;
            keys.add(c.firstName.trim().toLowerCase());
        }
    }
    return keys;
}
export function companionDedupContext(allGuests) {
    return {
        companionIds: collectCompanionGuestIds(allGuests),
        legacyNameKeys: collectLegacyCompanionNameKeys(allGuests),
        legacyChildFirstNameKeys: collectLegacyChildFirstNameKeys(allGuests),
    };
}
function countGuestsWithNameKey(guests, key) {
    return guests.filter((g) => personNameKey(g.firstName, g.lastName) === key).length;
}
function countGuestsWithFirstName(guests, firstName) {
    const key = firstName.trim().toLowerCase();
    return guests.filter((g) => g.firstName.trim().toLowerCase() === key).length;
}
/** True when this guest should appear only as a companion/child sub-row, not as a host. */
export function isGuestShownAsCompanion(guest, allGuests, companionIds = collectCompanionGuestIds(allGuests), legacyNameKeys = collectLegacyCompanionNameKeys(allGuests), legacyChildFirstNameKeys = collectLegacyChildFirstNameKeys(allGuests)) {
    if (companionIds.has(guest.id))
        return true;
    const key = personNameKey(guest.firstName, guest.lastName);
    if (legacyNameKeys.has(key) && countGuestsWithNameKey(allGuests, key) === 1) {
        return true;
    }
    const childFirst = guest.firstName.trim().toLowerCase();
    if (legacyChildFirstNameKeys.has(childFirst) &&
        countGuestsWithFirstName(allGuests, childFirst) === 1) {
        return true;
    }
    return false;
}
/** Guest docs that should produce a standalone host row in the list. */
export function guestsForHostRows(allGuests) {
    const ctx = companionDedupContext(allGuests);
    const canonicalCoupleIds = getCanonicalCoupleIds(allGuests);
    return allGuests.filter((g) => !(isWeddingCoupleGuest(g) && !canonicalCoupleIds.has(g.id)) &&
        !isGuestShownAsCompanion(g, allGuests, ctx.companionIds, ctx.legacyNameKeys, ctx.legacyChildFirstNameKeys));
}
const countNamedChildren = (g) => (g.children ?? []).filter((c) => c.firstName?.trim()).length;
/** Invitation hosts for stats — excludes the system wedding couple. */
function guestsForRsvpStats(guests) {
    return guestsForHostRows(guests).filter((g) => !isWeddingCoupleGuest(g));
}
/**
 * Total invited people across invitation hosts (host + plus-one + children).
 * Excludes linked companion/child guest docs and counts wedding-couple
 * children only — groom/bride themselves are not part of the guest cap.
 */
export function countInvitationHeads(guests) {
    let total = 0;
    for (const g of guestsForRsvpStats(guests)) {
        total += headCount(g);
    }
    for (const host of getWeddingCoupleGuests(guests)) {
        total += countNamedChildren(host);
    }
    return total;
}
